import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Reveal from "@/components/site/Reveal";
import { CATEGORIES, IMAGES } from "@/lib/content";
import { ArrowUpRight, Lock } from "lucide-react";

const SUBCATEGORIES = [
    { slug: "all", label: "All" },
    { slug: "footwear", label: "Footwear" },
    { slug: "jackets", label: "Jackets" },
    { slug: "bags", label: "Bags" },
    { slug: "belts", label: "Belts" },
    { slug: "wallets", label: "Wallets" },
    { slug: "custom", label: "Custom-Made" },
];

const WOMEN_CATEGORIES = [
    {
        slug: "w-shoes",
        title: "Shoes",
        sub: "Loafers · Mules · Boots",
        image: IMAGES.shoes,
    },
    {
        slug: "w-jackets",
        title: "Jackets",
        sub: "Trench · Bomber · Tailored",
        image: IMAGES.jacket,
    },
    {
        slug: "w-bags",
        title: "Bags",
        sub: "Hobo · Crossbody · Totes",
        image: IMAGES.bag,
    },
];

const MOODBOARD_COLLECTION = [
    {
        src: IMAGES.shoes,
        cat: "Footwear",
        sub: "Formal · Oxfords",
        gender: "Men",
    },
    {
        src: "/brand/WhatsApp Image 2026-04-30 at 14.15.06.jpeg",
        cat: "Footwear",
        sub: "Formal · Slip-ons",
        gender: "Men",
    },
    {
        src: "/brand/WhatsApp Image 2026-04-30 at 14.15.05.jpeg",
        cat: "Bags",
        sub: "Handwoven Laptop Bag",
        gender: "Unisex",
    },
    {
        src: "/brand/WhatsApp Image 2026-04-30 at 14.15.07.jpeg",
        cat: "Footwear",
        sub: "Sandals · Gladiators",
        gender: "Men",
    },
    {
        src: IMAGES.heroMacro,
        cat: "Craft",
        sub: "Saddle Stitch",
        gender: "House",
    },
    {
        src: "brand/WhatsApp Image 2026-04-30 at 14.15.13.jpeg",
        cat: "Footwear",
        sub: "Formal · Derbies",
        gender: "Men",
    },
    {
        src: "brand/WhatsApp Image 2026-04-30 at 14.15.06 (1).jpeg",
        cat: "Footwear",
        sub: "Slip Ons · Loafers",
        gender: "Men",
    },
    {
        src: "brand/launch-product.png",
        cat: "Bags",
        sub: "Cognac",
        gender: "Unisex",
    },
    {
        src: "brand/slipons.jpeg",
        cat: "Footwear",
        sub: "Slip-ons · Loafers",
        gender: "Unisex",
    },
    {
        src: "/brand/boots.jpeg",
        cat: "Footwear",
        sub: "Boots",
        gender: "Men",
    },
    {
        src: "/brand/LeatherLoafers.jpeg",
        cat: "Footwear",
        sub: "Loafers · Croc-embossed",
        gender: "Men",
    },
    {
        src: "/brand/Shoess.jpeg",
        cat: "Footwear",
        sub: "Formal · Buckles",
        gender: "Men",
    },
    {
        src: "/brand/Boot.jpeg",
        cat: "Footwear",
        sub: "Boots · Chelsea",
        gender: "Men",
    },
];

export default function Collection() {
    const [gender, setGender] = useState("men");
    const [filter, setFilter] = useState("all");

    const items = useMemo(() => {
        if (filter === "all") return CATEGORIES;
        if (filter === "custom") return CATEGORIES.filter((c) => c.custom);
        return CATEGORIES.filter((c) => c.slug === filter);
    }, [filter]);

    return (
        <main data-testid="collection-page" className="bg-[#0a0a0a] text-white">
            {/* Intro */}
            <section className="px-6 sm:px-10 lg:px-16 pt-36 lg:pt-44 pb-16 lg:pb-20">
                <Reveal>
                    <p className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-8">
                        Collection · Five houses
                    </p>
                </Reveal>
                <Reveal delay={0.1}>
                    <h1 className="font-serif text-[14vw] sm:text-[10vw] lg:text-[8.4vw] font-medium leading-[0.9] tracking-tight max-w-6xl">
                        Everything
                        <br />
                        we make,
                        <br />
                        <em className="font-normal">in one place</em>.
                    </h1>
                </Reveal>
                <Reveal delay={0.2}>
                    <p className="mt-12 max-w-xl text-base text-white/60 font-light leading-relaxed">
                        Browse by gender, then by category. Filter to see only what we
                        draft to measure. Each piece is made in the Wheeler Road
                        atelier.
                    </p>
                </Reveal>
            </section>

            {/* Gender Tabs */}
            <section
                data-testid="gender-tabs"
                className="px-6 sm:px-10 lg:px-16 pb-2"
            >
                <div className="flex items-end gap-8 lg:gap-14 border-b border-white/10">
                    {[
                        { slug: "men", label: "Men", count: CATEGORIES.length },
                        {
                            slug: "women",
                            label: "Women",
                            count: WOMEN_CATEGORIES.length,
                            soon: true,
                        },
                    ].map((g) => (
                        <button
                            key={g.slug}
                            data-testid={`gender-tab-${g.slug}`}
                            onClick={() => setGender(g.slug)}
                            className={`relative pb-5 group transition-colors ${
                                gender === g.slug
                                    ? "text-white"
                                    : "text-white/40 hover:text-white/70"
                            }`}
                        >
                            <span className="font-serif text-4xl lg:text-6xl font-medium tracking-tight leading-none">
                                {g.label}
                            </span>
                            <span className="ml-3 text-[10px] uppercase tracking-[0.28em] text-white/40 align-top">
                                {g.soon ? "Soon" : `· ${g.count}`}
                            </span>
                            {gender === g.slug && (
                                <motion.span
                                    layoutId="gender-underline"
                                    className="absolute left-0 right-0 -bottom-px h-px bg-white"
                                    transition={{
                                        duration: 0.5,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                />
                            )}
                        </button>
                    ))}
                </div>
            </section>

            {/* Filter bar (men only) */}
            <AnimatePresence mode="wait">
                {gender === "men" && (
                    <motion.section
                        key="men-filter"
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.4 }}
                        data-testid="collection-filters"
                        className="sticky top-20 z-30 bg-[#0a0a0a]/85 backdrop-blur-xl border-b border-white/10 px-6 sm:px-10 lg:px-16 py-5 mt-2"
                    >
                        <div className="flex items-center gap-6 overflow-x-auto no-scrollbar">
                            {SUBCATEGORIES.map((s) => (
                                <button
                                    key={s.slug}
                                    data-testid={`filter-${s.slug}`}
                                    onClick={() => setFilter(s.slug)}
                                    className={`whitespace-nowrap text-[11px] uppercase tracking-[0.28em] py-2 border-b transition-all duration-400 ${
                                        filter === s.slug
                                            ? "text-white border-white"
                                            : "text-white/50 border-transparent hover:text-white"
                                    }`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    </motion.section>
                )}
            </AnimatePresence>

            {/* Grid */}
            <AnimatePresence mode="wait">
                {gender === "men" ? (
                    <motion.section
                        key="men-grid"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="px-6 sm:px-10 lg:px-16 py-20 lg:py-28"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
                            {items.map((cat, i) => (
                                <Reveal
                                    key={cat.slug}
                                    delay={(i % 3) * 0.08}
                                    y={50}
                                    className="group"
                                >
                                    <Link
                                        to="/#appointment"
                                        data-testid={`collection-item-${cat.slug}`}
                                        className="block"
                                    >
                                        <div className="relative aspect-[3/4] overflow-hidden bg-[#111]">
                                            <img
                                                src={cat.image}
                                                alt={cat.title}
                                                className="w-full h-full object-cover grayscale transition-all duration-[1400ms] ease-out group-hover:scale-105 group-hover:grayscale-0"
                                            />
                                            <div className="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-700" />

                                            {cat.custom && (
                                                <div
                                                    className="absolute top-5 left-5 px-3 py-1 border border-white/60 text-[9px] uppercase tracking-[0.28em] text-white bg-black/30 backdrop-blur-sm"
                                                    data-testid={`custom-tag-${cat.slug}`}
                                                >
                                                    Custom-Made
                                                </div>
                                            )}

                                            <div className="absolute top-5 right-5 text-[10px] uppercase tracking-[0.28em] text-white/60 font-mono-custom">
                                                0{i + 1}
                                            </div>
                                        </div>

                                        <div className="pt-6 flex items-start justify-between gap-4">
                                            <div>
                                                <h3 className="font-serif text-3xl font-medium tracking-tight leading-none">
                                                    {cat.title}
                                                </h3>
                                                <p className="mt-3 text-[11px] uppercase tracking-[0.28em] text-white/50">
                                                    {cat.sub}
                                                </p>
                                                <p className="mt-4 text-sm text-white/60 font-light leading-relaxed max-w-sm">
                                                    {cat.description}
                                                </p>
                                                <p className="mt-5 text-[10px] uppercase tracking-[0.28em] text-white/40 font-mono-custom">
                                                    {cat.count}
                                                </p>
                                            </div>
                                            <span className="mt-1 shrink-0 w-10 h-10 rounded-full border border-white/30 flex items-center justify-center transition-all duration-500 group-hover:bg-white group-hover:text-black group-hover:rotate-45">
                                                <ArrowUpRight size={14} />
                                            </span>
                                        </div>
                                    </Link>
                                </Reveal>
                            ))}
                        </div>
                    </motion.section>
                ) : (
                    <motion.section
                        key="women-grid"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="px-6 sm:px-10 lg:px-16 py-20 lg:py-28"
                    >
                        <div className="mb-12 lg:mb-16 max-w-2xl">
                            <Reveal>
                                <p className="text-[11px] uppercase tracking-[0.3em] text-[#d5aa78] mb-5">
                                    The Women's House · Coming Soon
                                </p>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[0.95] tracking-tight">
                                    A new chapter,
                                    <br />
                                    <em className="font-normal">in the works</em>.
                                </h2>
                            </Reveal>
                            <Reveal delay={0.2}>
                                <p className="mt-6 text-base text-white/60 font-light leading-relaxed">
                                    Shoes, jackets and bags drafted for women — in
                                    measured collaboration with our atelier. Sign up at
                                    your next visit to be the first to be fitted.
                                </p>
                            </Reveal>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
                            {WOMEN_CATEGORIES.map((cat, i) => (
                                <Reveal
                                    key={cat.slug}
                                    delay={i * 0.08}
                                    y={50}
                                    className="group"
                                >
                                    <div
                                        data-testid={`women-item-${cat.slug}`}
                                        className="block cursor-not-allowed"
                                    >
                                        <div className="relative aspect-[3/4] overflow-hidden bg-[#111]">
                                            <img
                                                src={cat.image}
                                                alt={cat.title}
                                                className="w-full h-full object-cover grayscale opacity-50 transition-all duration-[1400ms] ease-out group-hover:opacity-70"
                                            />
                                            <div className="absolute inset-0 bg-black/40" />
                                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                                                <Lock
                                                    size={18}
                                                    className="text-white/80"
                                                />
                                                <p
                                                    className="text-[10px] uppercase tracking-[0.32em] text-white/80 font-mono-custom"
                                                    data-testid={`coming-soon-${cat.slug}`}
                                                >
                                                    Coming Soon
                                                </p>
                                            </div>
                                        </div>

                                        <div className="pt-6">
                                            <h3 className="font-serif text-3xl font-medium tracking-tight leading-none">
                                                {cat.title}
                                            </h3>
                                            <p className="mt-3 text-[11px] uppercase tracking-[0.28em] text-white/50">
                                                {cat.sub}
                                            </p>
                                        </div>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </motion.section>
                )}
            </AnimatePresence>

            {/* Moodboard Collection */}
            <section
                data-testid="collection-moodboard"
                className="px-6 sm:px-10 lg:px-16 py-24 lg:py-32 border-t border-white/10"
            >
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14 lg:mb-20">
                    <div className="max-w-3xl">
                        <Reveal>
                            <p className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-6">
                                Related · Gallery
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[0.95] tracking-tight">
                                Frames from
                                <br />
                                the archive.
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.2}>
                        <p className="max-w-sm text-sm text-white/55 font-light leading-relaxed">
                            A loose collage of work, leather, and people. Hover to read
                            the tag — each frame ties back to a category and a hand
                            that made it.
                        </p>
                    </Reveal>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6 auto-rows-[36vh] lg:auto-rows-[40vh]">
                    {MOODBOARD_COLLECTION.map((m, i) => {
                        const spans = [
                            "row-span-2",
                            "",
                            "",
                            "row-span-2",
                            "",
                            "",
                            "row-span-2 col-span-2 md:col-span-1",
                            "",
                        ];
                        return (
                            <Reveal
                                key={i}
                                delay={(i % 4) * 0.08}
                                y={40}
                                className={`relative group overflow-hidden ${spans[i] || ""}`}
                            >
                                <div
                                    data-testid={`coll-mood-${i}`}
                                    className="relative w-full h-full"
                                >
                                    <img
                                        src={m.src}
                                        alt={`${m.cat} · ${m.sub}`}
                                        className="absolute inset-0 w-full h-full object-cover grayscale transition-all duration-[1400ms] ease-out group-hover:scale-105 group-hover:grayscale-0"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-90" />

                                    <div className="absolute top-4 right-4 px-2.5 py-1 bg-black/40 backdrop-blur-sm border border-white/20 text-[9px] uppercase tracking-[0.3em] text-white/85 font-mono-custom">
                                        {m.gender}
                                    </div>

                                    <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
                                        <div className="transition-transform duration-700 group-hover:-translate-y-0.5">
                                            <p className="text-[9px] uppercase tracking-[0.32em] text-white/55 font-mono-custom">
                                                {m.cat}
                                            </p>
                                            <p className="mt-1 font-serif text-base lg:text-lg text-white tracking-tight leading-tight">
                                                {m.sub}
                                            </p>
                                        </div>
                                        <span className="hidden md:inline-flex shrink-0 w-7 h-7 rounded-full border border-white/40 items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:rotate-45">
                                            <ArrowUpRight
                                                size={11}
                                                className="text-white"
                                            />
                                        </span>
                                    </div>
                                </div>
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            {/* CTA */}
            <section className="px-6 sm:px-10 lg:px-16 pb-28 lg:pb-40">
                <Reveal y={40}>
                    <div className="border-t border-white/10 pt-16 lg:pt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                        <div className="lg:col-span-8">
                            <p className="text-[11px] uppercase tracking-[0.3em] text-white/50 mb-6">
                                Not listed?
                            </p>
                            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[0.95] tracking-tight">
                                If you can describe it,
                                <br />
                                we can likely{" "}
                                <em className="font-normal">make it.</em>
                            </h2>
                        </div>
                        <div className="lg:col-span-4 lg:text-right">
                            <Link
                                to="/#appointment"
                                data-testid="collection-cta-book"
                                className="group inline-flex items-center gap-4 border border-white/40 px-10 py-4 text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-black transition-all duration-500"
                            >
                                Book a consultation
                                <ArrowUpRight
                                    size={14}
                                    className="transition-transform duration-500 group-hover:rotate-45"
                                />
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>
        </main>
    );
}
